"use client";

import { useEffect, useMemo } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { useState } from "react";
import { GripVertical } from "lucide-react";
import clsx from "clsx";
import { FgInput } from "@/components/fg-input";
import { FgToggle } from "@/components/fg-toggle";
import { AiAssistButton } from "../AiAssistButton";
import { FormValidityBanner } from "../FormValidityBanner";
import { MobilePreview } from "../MobilePreview";

export interface OrderingBuilderProps {
  prompt: string;
  onPromptChange: (next: string) => void;
  /**
   * Canonical sentence. When the admin types here, we auto-populate
   * `bank` with one chip per word (or per punctuation token if `tokenizePunctuation` is on).
   */
  canonicalSentence: string;
  onCanonicalSentenceChange: (next: string) => void;
  /** Tokens not yet placed in the sentence rail. */
  bank: string[];
  onBankChange: (next: string[]) => void;
  /** Tokens in the order the learner must arrange them. */
  rail: string[];
  onRailChange: (next: string[]) => void;
  tokenizePunctuation: boolean;
  onTokenizePunctuationChange: (next: boolean) => void;
  explanation?: string;
  onExplanationChange?: (next: string) => void;
  level: "A1" | "A2" | "B1" | "B2" | "C1";
}

const PUNCT = /([.,;:!?])/;

function tokenize(sentence: string, withPunct: boolean): string[] {
  const trimmed = sentence.trim();
  if (!trimmed) return [];
  if (withPunct) {
    // Split words + keep punctuation as own tokens.
    return trimmed
      .split(/\s+/)
      .flatMap((word) => word.split(PUNCT).filter(Boolean))
      .filter(Boolean);
  }
  return trimmed.split(/\s+/).filter(Boolean);
}

export function OrderingBuilder({
  prompt,
  onPromptChange,
  canonicalSentence,
  onCanonicalSentenceChange,
  bank,
  onBankChange,
  rail,
  onRailChange,
  tokenizePunctuation,
  onTokenizePunctuationChange,
  explanation,
  onExplanationChange,
  level,
}: OrderingBuilderProps) {
  const validity = useMemo(
    () => [
      {
        label: "Prompt written",
        severity: prompt.trim() ? ("ok" as const) : ("error" as const),
      },
      {
        label: "Canonical sentence written",
        severity: canonicalSentence.trim()
          ? ("ok" as const)
          : ("error" as const),
      },
      {
        label: `At least 3 tokens (have ${bank.length + rail.length})`,
        severity:
          bank.length + rail.length >= 3
            ? ("ok" as const)
            : ("error" as const),
      },
      {
        label: "Tokens placed in the rail",
        severity:
          rail.length > 0 && bank.length === 0
            ? ("ok" as const)
            : ("warn" as const),
      },
    ],
    [prompt, canonicalSentence, bank, rail],
  );

  // Auto-build bank from canonical sentence (with light shuffle) when
  // sentence changes.
  useEffect(() => {
    const tokens = tokenize(canonicalSentence, tokenizePunctuation);
    if (tokens.length === 0) return;
    // Light deterministic shuffle — keep first token first (capitalisation hint)
    // and randomise the rest.
    const first = tokens[0];
    const rest = tokens.slice(1);
    for (let i = rest.length - 1; i > 0; i--) {
      const j = (i * 31 + (canonicalSentence.length * 7)) % (i + 1);
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    const shuffled = [first, ...rest];
    onBankChange(shuffled);
    onRailChange([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalSentence, tokenizePunctuation]);

  function moveToRail(token: string) {
    onBankChange(bank.filter((t) => t !== token));
    onRailChange([...rail, token]);
  }

  function moveToBank(token: string) {
    onRailChange(rail.filter((t) => t !== token));
    onBankChange([...bank, token]);
  }

  function reorderRail(fromIdx: number, toIdx: number) {
    const next = [...rail];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    onRailChange(next);
  }

  function applyAiTokens(content: unknown) {
    if (!Array.isArray(content)) return;
    const next = content.map((t) => String(t));
    if (next.length === 0) return;
    const correct = tokenize(canonicalSentence, tokenizePunctuation);
    if (correct.length === 0) return;
    // Use Gemini's tokens as the bank if they roughly match length; otherwise
    // fall back to a shuffle of the canonical sentence.
    if (Math.abs(next.length - correct.length) <= 1) {
      onBankChange(next);
      onRailChange([]);
    } else {
      const shuffled = [...correct];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = (i * 17) % (i + 1);
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      onBankChange(shuffled);
      onRailChange([]);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <FormValidityBanner title="Word ordering" checks={validity} />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.6fr,1fr]">
        <div className="flex flex-col gap-3">
          <FgInput
            label="Prompt"
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            placeholder="Put the words in the right order."
          />
          <FgInput
            label="Canonical sentence (auto-builds the token bank)"
            value={canonicalSentence}
            onChange={(e) => onCanonicalSentenceChange(e.target.value)}
            placeholder="Ich lerne Deutsch."
            hint="Edit this to regenerate the bank below."
          />
          <FgToggle
            label="Tokenize punctuation (commas / periods become draggable chips)"
            checked={tokenizePunctuation}
            onChange={onTokenizePunctuationChange}
          />
        </div>
        <MobilePreview
          kind="ordering"
          prompt={prompt}
          items={rail.length > 0 ? rail : bank}
        />
      </div>

      <AiAssistButton
        kind="tokens"
        prompt={canonicalSentence || "Satz"}
        level={level}
        label="Regenerate bank (shuffle)"
        onApply={applyAiTokens}
        disabled={!canonicalSentence.trim()}
      />

      <OrderingWorkbench
        bank={bank}
        rail={rail}
        onMoveToRail={moveToRail}
        onMoveToBank={moveToBank}
        onReorderRail={reorderRail}
      />

      {onExplanationChange && (
        <FgInput
          label="Explanation (optional)"
          value={explanation ?? ""}
          onChange={(e) => onExplanationChange(e.target.value)}
        />
      )}
    </div>
  );
}

/**
 * Two-column DnD workspace:
 *   - Bank:  drag tokens → Rail
 *   - Rail:  drag tokens → Bank (removes), or reorder within Rail
 *
 * Uses `useDraggable` + `useDroppable` directly (not SortableList) so chips
 * can cross between two containers.
 */
function OrderingWorkbench({
  bank,
  rail,
  onMoveToRail,
  onMoveToBank,
  onReorderRail,
}: {
  bank: string[];
  rail: string[];
  onMoveToRail: (token: string) => void;
  onMoveToBank: (token: string) => void;
  onReorderRail: (from: number, to: number) => void;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  function handleDragStart(e: DragStartEvent) {
    setActiveId(String(e.active.id));
  }
  function handleDragEnd(e: DragEndEvent) {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (overId === "bank") {
      if (rail.some((t) => tokenKey(t) === activeId)) {
        onMoveToBank(activeId);
      }
      return;
    }
    if (overId === "rail") {
      if (bank.some((t) => tokenKey(t) === activeId)) {
        onMoveToRail(activeId);
      }
      return;
    }
    // Reorder within the rail.
    const fromIdx = rail.findIndex((t) => tokenKey(t) === activeId);
    const toIdx = rail.findIndex((t) => tokenKey(t) === overId);
    if (fromIdx >= 0 && toIdx >= 0 && fromIdx !== toIdx) {
      onReorderRail(fromIdx, toIdx);
    }
  }

  const activeToken = activeId
    ? bank.find((t) => tokenKey(t) === activeId) ?? rail.find((t) => tokenKey(t) === activeId)
    : null;

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <TokenColumn
          label={`Token bank (${bank.length})`}
          empty="All tokens are placed in the rail"
          tone="default"
          tokens={bank}
          onChipClick={onMoveToRail}
          dropZoneId="bank"
        />
        <TokenColumn
          label={`Sentence rail (${rail.length})`}
          empty="Drag tokens here to build the sentence"
          tone="primary"
          tokens={rail}
          onChipClick={onMoveToBank}
          dropZoneId="rail"
        />
      </div>
      <DragOverlay>
        {activeToken ? (
          <TokenChip token={activeToken} isOverlay />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function TokenColumn({
  label,
  tokens,
  empty,
  tone,
  onChipClick,
  dropZoneId,
}: {
  label: string;
  tokens: string[];
  empty: string;
  tone: "default" | "primary";
  onChipClick: (id: string) => void;
  dropZoneId: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dropZoneId });
  const isPrimary = tone === "primary";

  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
        {label}
      </p>
      <div
        ref={setNodeRef}
        className={clsx(
          "min-h-[120px] rounded-lg border-2 border-dashed p-2 transition-colors",
          isOver
            ? "border-[#FFD60A] bg-[#FFD60A]/10"
            : isPrimary
              ? "border-[rgba(255,255,255,0.1)] bg-[#141414]"
              : "border-[rgba(255,255,255,0.06)] bg-[#0A0A0A]/40",
        )}
      >
        {tokens.length === 0 ? (
          <p className="text-[11px] font-mono text-[#8E8E93] text-center py-6">{empty}</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {tokens.map((t) => (
              <TokenChip
                key={tokenKey(t)}
                token={t}
                onClick={() => onChipClick(tokenKey(t))}
                isPrimary={isPrimary}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function tokenKey(token: string): string {
  // Unique enough for dnd-kit while remaining stable across renders.
  return `tok:${token}`;
}

function TokenChip({
  token,
  onClick,
  isPrimary,
  isOverlay,
}: {
  token: string;
  onClick?: () => void;
  isPrimary?: boolean;
  isOverlay?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: tokenKey(token),
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;
  return (
    <button
      type="button"
      ref={setNodeRef}
      style={style}
      onClick={onClick}
      {...attributes}
      {...listeners}
      className={clsx(
        "inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-sm font-mono cursor-grab active:cursor-grabbing select-none",
        isPrimary
          ? "bg-[#FFD60A] text-[#0A0A0A] border-[#FFD60A] font-bold"
          : "bg-[#1C1C1E] text-[#FAFAFA] border-[rgba(255,255,255,0.08)]",
        (isDragging || isOverlay) && "shadow-[0_8px_24px_rgba(0,0,0,0.5)] opacity-90",
      )}
      aria-label={`Token "${token}" — drag to reorder, click to move`}
    >
      <GripVertical size={11} className={isPrimary ? "text-[#0A0A0A]/60" : "text-[#8E8E93]"} />
      {token}
    </button>
  );
}